/**
 * Delete one Turnkey session key by its public key.
 *
 * Every login and every `refreshSession()` mints a new expiring API key on the
 * Turnkey user and never removes the one it replaces. Turnkey keeps at most
 * ten expiring keys per user and silently deletes the oldest live one when an
 * eleventh arrives, so a browser whose stored session still reads as valid
 * loses its key mid-week and every signature fails until the person logs out
 * and back in. Pruning the key a refresh (or logout) has just retired keeps
 * each device at one key, so the cap is never reached.
 *
 * Best effort: the caller decides what a failure means (the session it just
 * minted is unaffected either way). Platform-neutral — the kit's `httpClient`
 * on web and React Native both satisfy `SessionKeyClient`.
 */

export interface SessionKeyClient {
  readonly getApiKeys: (input: {
    readonly userId: string;
  }) => Promise<{ readonly apiKeys: readonly ApiKeyLike[] }>;
  readonly deleteApiKeys: (input: {
    readonly userId: string;
    readonly apiKeyIds: string[];
  }) => Promise<unknown>;
}

interface ApiKeyLike {
  readonly apiKeyId: string;
  readonly credential: { readonly publicKey: string };
}

export interface SessionKeyRef {
  readonly userId: string;
  readonly publicKey: string;
}

/** Returns true when a key with that public key existed and was deleted. */
export async function pruneSessionKey(
  client: SessionKeyClient,
  key: SessionKeyRef,
): Promise<boolean> {
  const { apiKeys } = await client.getApiKeys({ userId: key.userId });
  const match = apiKeys.find((k) => k.credential.publicKey === key.publicKey);
  if (match === undefined) return false;
  await client.deleteApiKeys({ userId: key.userId, apiKeyIds: [match.apiKeyId] });
  return true;
}
